import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { signOut } from 'firebase/auth';
import { auth, db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { LogOut, UserPlus, LogIn, PlusCircle, GitBranch, User, FlaskConical, ShieldCheck, Globe, Menu, X, BookOpen } from 'lucide-react';
import Logo from './Logo';
import NotificationBell from './NotificationBell';

export default function Navbar() {
  const { currentUser, initializing } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isVerifier, setIsVerifier] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Check if user is a verifier
  useEffect(() => {
    // Wait for auth to initialize
    if (initializing) return;

    if (currentUser) {
      const verifierRef = doc(db, 'verifiers', currentUser.uid);
      const checkVerifier = async () => {
        const verifierSnap = await getDoc(verifierRef);
        setIsVerifier(verifierSnap.exists());
      };
      checkVerifier();
    } else {
      setIsVerifier(false);
    }
  }, [currentUser, initializing]);

  const handleLogout = async () => {
    await signOut(auth);
    navigate('/'); 
  };

  return (
    <header className="glass-card fixed top-0 left-0 right-0 z-50">
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center h-16">
          <Link to={currentUser ? "/home" : "/"} className="flex items-center gap-3">
            <Logo />
            <div>
              <span className="text-xl font-bold bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent">
                CFE Portal™
              </span>
              <span className="block text-xs text-slate-500 -mt-1">
                Crypto Fraud Exposé
              </span>
            </div>
          </Link>
          
          <nav className="flex items-center space-x-2">
            {/* Mobile hamburger button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="sm:hidden flex items-center justify-center text-slate-300 hover:text-white transition p-2"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
            </button>

            {/* --- THIS IS THE NEW EXTERNAL LINK --- */}
            <a
              href="https://qlabsai.com"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden sm:flex items-center gap-2 text-slate-300 hover:text-white transition text-sm font-medium px-4 py-2"
            >
              <FlaskConical size={16} />
              Qlabs
            </a>

            <Link
              to="/map"
              className="hidden sm:flex items-center gap-2 text-cyan-400 hover:text-cyan-300 transition text-sm font-medium px-4 py-2"
            >
              <Globe size={16} />
              Global Map
            </Link>

            <Link
              to="/matrix"
              className="hidden sm:flex items-center gap-2 text-slate-300 hover:text-white transition text-sm font-medium px-4 py-2"
            >
              <GitBranch size={16} />
              Fraud Matrix
            </Link>

            <Link
              to="/learn"
              className="hidden sm:flex items-center gap-2 text-slate-300 hover:text-white transition text-sm font-medium px-4 py-2"
            >
              <BookOpen size={16} />
              Learn
            </Link>

            {currentUser ? (
              <>
                <Link
                  to={`/profile/${currentUser.uid}`}
                  className="hidden sm:flex items-center gap-2 text-cyan-400 hover:text-cyan-300 transition text-sm font-medium px-4 py-2"
                >
                  <User size={16} />
                  {currentUser.displayName || 'Profile'}
                </Link>
                {isVerifier && (
                  <Link
                    to="/verify"
                    className="hidden sm:flex items-center gap-2 text-green-400 hover:text-green-300 transition text-sm font-medium px-4 py-2"
                    title="Verification Dashboard"
                  >
                    <ShieldCheck size={16} />
                    Verify
                  </Link>
                )}
                <NotificationBell />
                <Link
                  to="/submit"
                  className="flex items-center gap-2 bg-cyan-500/90 hover:bg-cyan-400 text-black font-bold py-2 px-4 rounded-lg text-sm transition-colors"
                >
                  <PlusCircle size={16} />
                  Create Report
                </Link>
                <button 
                  onClick={handleLogout} 
                  className="flex items-center gap-2 bg-slate-700 hover:bg-red-500/80 text-white font-bold py-2 px-3 rounded-lg text-sm transition-colors"
                >
                  <LogOut size={16} />
                  <span className="hidden sm:inline">Logout</span>
                </button>
              </>
            ) : (
              <>
                <Link 
                  to="/login" 
                  className="hidden sm:flex items-center gap-2 text-slate-300 hover:text-white transition text-sm font-medium px-4 py-2"
                >
                  <LogIn size={16} />
                  Login
                </Link>
                <Link 
                  to="/signup" 
                  className="flex items-center gap-2 bg-slate-100 hover:bg-white text-slate-900 font-bold py-2 px-4 rounded-lg text-sm transition-colors"
                >
                  <UserPlus size={16} />
                  Sign Up
                </Link>
              </>
            )}
          </nav>
        </div>
      </div>

      {/* Mobile menu panel */}
      {mobileMenuOpen && (
        <div className="sm:hidden border-t border-white/10 px-4 py-2">
          <a
            href="https://qlabsai.com"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 text-slate-300 hover:text-white transition text-sm font-medium px-2 py-3 border-b border-white/5"
            onClick={() => setMobileMenuOpen(false)}
          >
            <FlaskConical size={18} />
            Qlabs
          </a>
          <Link
            to="/map"
            className="flex items-center gap-3 text-cyan-400 hover:text-cyan-300 transition text-sm font-medium px-2 py-3 border-b border-white/5"
            onClick={() => setMobileMenuOpen(false)}
          >
            <Globe size={18} />
            Global Map
          </Link>
          <Link
            to="/matrix"
            className="flex items-center gap-3 text-slate-300 hover:text-white transition text-sm font-medium px-2 py-3 border-b border-white/5"
            onClick={() => setMobileMenuOpen(false)}
          >
            <GitBranch size={18} />
            Fraud Matrix
          </Link>
          <Link
            to="/learn"
            className="flex items-center gap-3 text-slate-300 hover:text-white transition text-sm font-medium px-2 py-3 border-b border-white/5"
            onClick={() => setMobileMenuOpen(false)}
          >
            <BookOpen size={18} />
            Learn
          </Link>
          {currentUser ? (
            <>
              <Link
                to={`/profile/${currentUser.uid}`}
                className="flex items-center gap-3 text-cyan-400 hover:text-cyan-300 transition text-sm font-medium px-2 py-3 border-b border-white/5"
                onClick={() => setMobileMenuOpen(false)}
              >
                <User size={18} />
                {currentUser.displayName || 'Profile'}
              </Link>
              {isVerifier && (
                <Link
                  to="/verify"
                  className="flex items-center gap-3 text-green-400 hover:text-green-300 transition text-sm font-medium px-2 py-3 border-b border-white/5"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <ShieldCheck size={18} />
                  Verification Dashboard
                </Link>
              )}
            </>
          ) : (
            <Link
              to="/login"
              className="flex items-center gap-3 text-slate-300 hover:text-white transition text-sm font-medium px-2 py-3"
              onClick={() => setMobileMenuOpen(false)}
            >
              <LogIn size={18} />
              Login
            </Link>
          )}
        </div>
      )}
    </header>
  );
}
